import { useState } from "react";
import { Calculator, ShieldCheck } from "lucide-react";
import { useI18n } from "../i18n";
import { trackSimulator } from "../lib/analytics";
import { useGoogleReviews } from "../lib/useGoogleReviews";
import { CONTACT } from "../lib/contact";

const EURIBOR_6M = 2.75; // indicativa — atualizar manualmente quando necessário

function calcPrestacao(montante, anos, spread) {
  const taxa = (EURIBOR_6M + spread) / 100 / 12;
  const n = anos * 12;
  if (taxa === 0) return montante / n;
  return (montante * taxa * Math.pow(1 + taxa, n)) / (Math.pow(1 + taxa, n) - 1);
}

const fillStyle = (val, min, max) => {
  const pct = ((val - min) / (max - min)) * 100;
  return {
    background: `linear-gradient(to right, #A60056 ${pct}%, rgba(255,255,255,0.12) ${pct}%)`,
  };
};

const Slider = ({ label, value, display, min, max, step, onChange, testid }) => (
  <div>
    <div className="flex justify-between mb-2">
      <span className="text-xs text-white/55 uppercase tracking-wider">{label}</span>
      <span className="text-sm font-semibold text-white">{display}</span>
    </div>
    <input
      type="range"
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      className="range-magenta w-full"
      style={fillStyle(value, min, max)}
      data-testid={testid}
    />
  </div>
);

export const MortgageSimulator = ({ showAdvisor = true }) => {
  const { t, lang } = useI18n();
  const gr = useGoogleReviews();
  const s = t.heroSim;
  const [montante, setMontante] = useState(250000);
  const [anos, setAnos] = useState(30);
  const [spread, setSpread] = useState(1.0);
  const prestacao = calcPrestacao(montante, anos, spread);
  const tan = EURIBOR_6M + spread;

  const goToContact = () => {
    trackSimulator("cta", { montante, anos, spread });
    const el = document.getElementById("contact");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="relative w-full max-w-md" data-testid="hero-simulator">
      <div className="absolute -inset-6 rounded-[2.5rem] bg-gradient-to-br from-[#A60056]/40 via-[#AC1361]/20 to-transparent blur-3xl opacity-70" />
      <div className="relative rounded-[1.75rem] p-6 md:p-7 bg-[#0A0A0C]/75 backdrop-blur-2xl border border-white/10 shadow-[0_25px_80px_rgba(166,0,86,0.25)]">
        {showAdvisor && (
          <div className="flex items-center gap-3 mb-6 pb-6 border-b border-white/10">
            <div className="relative flex-shrink-0">
              <img
          src="/diogo-perfil.png"
                alt={lang === "pt" ? "Diogo Almeida — Gestor de Crédito" : "Diogo Almeida — Mortgage Advisor"}
                className="w-12 h-12 rounded-full object-cover object-top border border-white/15"
                loading="eager"
              />
              <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-green-400 border-2 border-[#0A0A0C]" />
            </div>
            <div className="min-w-0">
              <div className="text-white font-medium leading-tight">Diogo Almeida</div>
              <div className="text-[11px] uppercase tracking-[0.14em] text-white/50 truncate">
                {t.hero.role}
              </div>
            </div>
            <a
              href={CONTACT.gmaps}
              target="_blank"
              rel="noreferrer"
              className="ml-auto text-[10px] uppercase tracking-[0.14em] text-[#A60056] font-bold text-right leading-tight hover:underline"
              data-testid="hero-google-rating"
            >
              ★ {gr
                ? `${(lang === "pt" ? gr.rating.toFixed(1).replace(".", ",") : gr.rating.toFixed(1))} ${lang === "pt" ? "no Google" : "on Google"} · ${gr.total} ${lang === "pt" ? "avaliações" : "reviews"}`
                : t.hero.recommend}
            </a>
          </div>
        )}

        <div className="flex items-center gap-2 mb-5">
          <Calculator className="w-4 h-4 text-[#A60056]" />
          <span className="text-sm font-medium text-white/85">{s.title}</span>
          <span className="text-[11px] text-white/40 ml-auto">
            {s.euribor}: {EURIBOR_6M.toFixed(2)}%
          </span>
        </div>

        <div className="space-y-5">
          <Slider
            label={s.amount}
            value={montante}
            display={`${montante.toLocaleString("pt-PT")} €`}
            min={50000}
            max={1000000}
            step={10000}
            onChange={setMontante}
            testid="simulator-amount-slider"
          />
          <Slider
            label={s.term}
            value={anos}
            display={`${anos} ${s.years}`}
            min={10}
            max={40}
            step={1}
            onChange={setAnos}
            testid="simulator-term-slider"
          />
          <Slider
            label={s.spread}
            value={spread}
            display={`${spread.toFixed(2)}%`}
            min={0.5}
            max={2.5}
            step={0.05}
            onChange={setSpread}
            testid="simulator-spread-slider"
          />
        </div>

        <div className="mt-6 p-5 rounded-2xl bg-gradient-to-br from-[#A60056]/15 to-[#AC1361]/5 border border-[#A60056]/25">
          <div className="text-[11px] text-white/50 uppercase tracking-wider mb-1">{s.monthly}</div>
          <div className="text-4xl font-semibold text-white tracking-tight" data-testid="simulator-result">
            {prestacao.toLocaleString("pt-PT", { maximumFractionDigits: 0 })} €
            <span className="text-base text-white/40 font-normal">{s.perMonth}</span>
          </div>
          <div className="text-[11px] text-white/40 mt-1">
            {s.rateNote}: {tan.toFixed(2)}%
          </div>
        </div>

        <button
          type="button"
          onClick={goToContact}
          className="btn-primary w-full mt-5"
          data-testid="simulator-cta"
        >
          <ShieldCheck className="w-4 h-4" />
          {s.cta}
        </button>
        <p className="text-[10px] text-white/25 mt-3 leading-relaxed">{s.disclaimer}</p>
      </div>
    </div>
  );
};

export default MortgageSimulator;
